import { FieldValues } from "react-hook-form";
import { useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";

import LoadingSpinner from "../LoadingSpinner";

import { ANIMATION_TYPE_CONFIG } from "./animations";
import { Step } from "./Step";
import { StepperProps } from "./types";
import { SidebarLayout } from "./SidebarLayout";
import { StepProps } from "./Step/types";

export const Stepper = <T extends FieldValues>({
  activeStep,
  onSkip,
  onNext,
  onBack,
  isLoading = false,
  steps,
  isNextButtonLoading = false,
  onStepOptionSelect,
  onItemHover,
  size,
  isPreview,
  onExit,
  layout = "default",
  isBackButtonVisible = true,
  footer,
  title,
  description,
  overlayContent,
  className,
  isAnimated,
  animationType,
}: StepperProps<T>) => {
  const prevStepRef = useRef(activeStep);
  const direction = activeStep >= prevStepRef.current ? 1 : -1;

  useEffect(() => {
    prevStepRef.current = activeStep;
  }, [activeStep]);

  const groupedSteps = useMemo(() => {
    if (!steps) {
      return [];
    }

    const grouped = steps.reduce(
      (acc, step, index) => {
        const group = step.group || "Default";
        if (!acc[group]) {
          acc[group] = [];
        }
        acc[group].push({ ...step, originalIndex: index });
        return acc;
      },
      {} as Record<string, ((typeof steps)[0] & { originalIndex: number })[]>,
    );

    return Object.entries(grouped);
  }, [steps]);

  const totalSteps = steps.length;

  if (!steps) {
    return null;
  }

  const currentStep = steps[activeStep];

  const handleNext = () => {
    onNext(activeStep + 1, currentStep.id);
  };

  const handleBack = () => {
    onBack(activeStep - 1, currentStep.id);
  };

  const handleSkip = () => {
    (onSkip ?? handleNext)();
  };

  const basicProps = {
    size,
    isPreview,
    isBackButtonVisible,
    onItemHover,
    onExit,
    onStepOptionSelect,
    overlayContent,
    onBack: handleBack,
    onNext: handleNext,
    onSkip: handleSkip,
    isNextButtonLoading,
    layout,
  };

  const stepProps: StepProps<T> = {
    ...basicProps,
    stepNumber: activeStep + 1,
    step: currentStep,
    totalSteps,
    className,
  };

  if (layout === "sidebar") {
    return (
      <SidebarLayout
        footer={footer}
        title={title}
        description={description}
        activeStep={activeStep}
        isLoading={isLoading}
        steps={steps}
        groupedSteps={groupedSteps}
        {...basicProps}
      />
    );
  }

  const animConfig = isAnimated && animationType ? ANIMATION_TYPE_CONFIG[animationType] : null;

  return (
    <>
      {isLoading && (
        <div className="absolute left-1/2 top-1/2 z-10 flex h-full w-full -translate-x-1/2 -translate-y-1/2 items-center justify-center">
          <div className="absolute inset-0 bg-gray-50/30 backdrop-blur-sm" />
          <LoadingSpinner className="h-4 w-4" />
        </div>
      )}
      {animConfig ? (
        <motion.div
          key={currentStep.id}
          custom={direction}
          variants={animConfig.variants}
          initial="enter"
          animate="center"
        >
          <Step {...stepProps} />
        </motion.div>
      ) : (
        <Step {...stepProps} />
      )}
    </>
  );
};
